/**
 * SimTeach puzzle specs (issue #4675), stored one per document at
 * {@link simTeachPuzzleDocPath}. The webapp's scenario editor writes them;
 * simteach reads them with the Admin SDK and mirrors this schema in
 * `app/section_teaching/simteach_puzzles.py` and the per-seat `students` in
 * `app/agent/student_seed.py` (both are checked against
 * `simTeachPuzzleSeeds.json`).
 */
import {z} from 'zod';

import {StudentSeedsSchema} from './studentSeed';

/** Firestore subcollection (under `course/{courseId}`) holding the puzzles. */
export const SIMTEACH_PUZZLES_COLLECTION = 'simTeachPuzzles';

/** What a surprise does to the room while it is open. */
export const SimTeachPuzzleEffectSchema = z.enum(['silence']);

/** A surprise card's room effect. */
export type SimTeachPuzzleEffect = z.infer<typeof SimTeachPuzzleEffectSchema>;

/** Whether a card is an ordinary step or a scripted room complication. */
export const SimTeachPuzzleCardKindSchema = z.enum(['step', 'surprise']);

/** A card's kind. */
export type SimTeachPuzzleCardKind = z.infer<
  typeof SimTeachPuzzleCardKindSchema
>;

/** A puzzle or card id: lowercase kebab-case, safe as a document id. */
export const SimTeachPuzzleIdSchema = z
  .string()
  .regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    'Use lowercase letters, digits, and dashes',
  );

/** One card of a SimTeach puzzle. */
export const SimTeachPuzzleCardSchema = z.object({
  /** Stable id, unique within the puzzle. */
  id: SimTeachPuzzleIdSchema,
  /** What the teacher should do. */
  title: z.string().trim().min(1, 'Required'),
  /** A line of guidance under the title. Omitted: title only. */
  subtitle: z.string().optional(),
  /** Step or surprise. */
  kind: SimTeachPuzzleCardKindSchema,
  /**
   * What the teacher must do on a pause to settle the card, judged by the
   * evaluator. Omitted: only a manual override settles it.
   */
  criterion: z.string().optional(),
  /**
   * Who completes it: `teacher` (checked after each teacher pause) or `room`
   * (checked after each student line, e.g. "everyone has introduced
   * themselves").
   */
  completedBy: z.enum(['teacher', 'room']),
  /**
   * Teacher steps only: the criterion is an explanation judged over the
   * teacher's lines so far, taken together, rather than the latest line
   * alone, and a student's reply to a partial explanation does not fail it;
   * only moving on to a later step does. For a teach-back, where a step has
   * several parts and the student replies between them. Omitted: judged on
   * the latest line, and a reply to a partial attempt fails it.
   */
  cumulative: z.boolean().optional(),
  /** Surprise only: the earlier step whose settling as done reveals it. */
  after: z.string().optional(),
  /** Surprise only: what the room does while it is open. Omitted: nothing. */
  effect: SimTeachPuzzleEffectSchema.optional(),
  /**
   * Surprise only: seconds after the reveal at which an open card fails,
   * ending its effect. Omitted: no timeout.
   */
  timeoutSeconds: z.number().positive().optional(),
});

/** One card of a SimTeach puzzle. */
export type SimTeachPuzzleCard = z.infer<typeof SimTeachPuzzleCardSchema>;

/**
 * How a puzzle is presented: a `lesson` walks a scripted path of visible
 * cards; a `puzzle` gives a goal and leaves the path to the teacher.
 */
export const SimTeachPuzzleKindSchema = z.enum(['lesson', 'puzzle']);

/** A puzzle's presentation. */
export type SimTeachPuzzleKind = z.infer<typeof SimTeachPuzzleKindSchema>;

/** The `speaker` value for a teacher turn in an opening. */
export const SIMTEACH_PUZZLE_TEACHER_SPEAKER = 'teacher';

/** The `speaker` of an opening's silence turn: the room says nothing. */
export const SIMTEACH_PUZZLE_SILENCE_SPEAKER = 'silence';

/** One scripted turn the session's opening replays. */
export const SimTeachPuzzleOpeningTurnSchema = z
  .object({
    /**
     * {@link SIMTEACH_PUZZLE_TEACHER_SPEAKER},
     * {@link SIMTEACH_PUZZLE_SILENCE_SPEAKER}, or the speaking seat's first
     * name (case-insensitive).
     */
    speaker: z.string().trim().min(1, 'Required'),
    /** What was said. Omitted (or empty) for a silence turn. */
    line: z.string().trim().optional(),
  })
  .superRefine((turn, ctx) => {
    const isSilence =
      turn.speaker.toLowerCase() === SIMTEACH_PUZZLE_SILENCE_SPEAKER;
    const hasLine = (turn.line ?? '') !== '';
    if (isSilence && hasLine) {
      ctx.addIssue({
        code: 'custom',
        path: ['line'],
        message: 'Silence has no line',
      });
    }
    if (!isSilence && !hasLine) {
      ctx.addIssue({code: 'custom', path: ['line'], message: 'Required'});
    }
  });

/** One scripted turn the session's opening replays. */
export type SimTeachPuzzleOpeningTurn = z.infer<
  typeof SimTeachPuzzleOpeningTurnSchema
>;

/**
 * What winning a puzzle looks like. Teachers see only the outcome; the goal
 * check (simteach) reads all three, and a failed goal reveals its mustNot.
 */
export const SimTeachPuzzleGoalSchema = z.object({
  /** What should happen in the room. */
  outcome: z.string().trim().min(1, 'Required'),
  /**
   * Whose lines can show it: a student's name (only their lines count), or
   * "the room" (any line, the teacher's included).
   */
  evidence: z.string().trim().min(1, 'Required'),
  /** What breaks the goal if it happens before the outcome. Omitted: it never fails. */
  mustNot: z.string().trim().min(1).optional(),
  /**
   * The seat (lowercase first name) holding the misconception the section
   * leader works on: the card students' learner and the debrief's voice.
   * Omitted: no one is stuck (a room puzzle), so there is no debrief.
   */
  stuckSeat: z.string().trim().toLowerCase().min(1).optional(),
  /**
   * How strict the card-student goal check is: `easy` counts an idea said
   * back, even on the teacher's word; `hard` needs the reason in the
   * student's own words, so telling the answer doesn't win. Omitted: easy.
   */
  mode: z.enum(['easy', 'hard']).optional(),
});

/** What winning a puzzle looks like. */
export type SimTeachPuzzleGoal = z.infer<typeof SimTeachPuzzleGoalSchema>;

/** A SimTeach puzzle document (its id is the document id). */
/** Prefix of a deck created for one puzzle: `simteachpuzzle-<puzzleId>-<random>`. */
export const SIMTEACH_PUZZLE_DECK_ID_PREFIX = 'simteachpuzzle-';

/**
 * A twist the opening slide's twist chooser starts with, as if a driver had
 * just drawn it. Applied to the session's copy of the deck only.
 */
export const SimTeachPuzzleTwistSchema = z
  .object({
    /** The twist's id in the set the chooser reads. Not in the set: no preset. */
    id: z.string().trim().min(1),
    /** `revealed` shows the drawn twist; `ide` opens it in the IDE. Omitted: revealed. */
    view: z.enum(['revealed', 'ide']).optional(),
    /** With `ide`: the `main.py` it starts with. Omitted: the twist's own. */
    code: z.string().min(1).optional(),
  })
  .refine(twist => twist.code === undefined || twist.view === 'ide', {
    message: 'code needs view "ide"',
    path: ['code'],
  });

/** A preset twist. */
export type SimTeachPuzzleTwist = z.infer<typeof SimTeachPuzzleTwistSchema>;

/**
 * Which slides a session shows: a deck (the course default unless `deckId`
 * names one), or none at all.
 */
export const SimTeachPuzzleSlidesSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('deck'),
    /**
     * A deck under the course's slide decks. Omitted: the course's default
     * section deck.
     */
    deckId: z.string().trim().min(1).optional(),
    /**
     * The course whose slide decks hold `deckId`. Omitted: the puzzle
     * course's slides course.
     */
    courseId: z.string().trim().min(1).optional(),
    /**
     * The id of the slide the session opens on. Omitted, or a slide no
     * longer in the deck: the first visible slide.
     */
    openAt: z.string().trim().min(1).optional(),
    /**
     * With `openAt` on a twist chooser slide: the twist it starts with.
     * Omitted: the chooser starts as it does in section.
     */
    twist: SimTeachPuzzleTwistSchema.optional(),
    /**
     * Whether the session starts on the slides instead of the gallery of
     * students. Omitted: the gallery, with the slides a click away.
     */
    startOnSlides: z.boolean().optional(),
  }),
  z.object({
    /** No slides: the call runs in gallery view and nothing is on screen. */
    kind: z.literal('none'),
  }),
]);

/** A puzzle's slides setting. */
export type SimTeachPuzzleSlides = z.infer<typeof SimTeachPuzzleSlidesSchema>;

/** How a tailored puzzle was written. */
export const SimTeachPuzzleTailoredSourceSchema = z.enum([
  'admin',
  'generated',
]);

/**
 * Where a tailored puzzle came from: one moment of one section week's AI
 * feedback, and the section leaders it was made for.
 */
export const SimTeachPuzzleTailoredSchema = z.object({
  /** The section the moment came from. */
  sectionId: z.string().trim().min(1),
  /** The section week whose feedback named the moment. */
  sectionWeek: z.number().int().positive(),
  /**
   * The uids who see it while it's unpublished: the section leaders who
   * taught that week.
   */
  forUids: z.array(z.string().trim().min(1)),
  /** The feedback's moment, as `HH:MM:SS` offsets into the recording. */
  moment: z.object({start: z.string(), end: z.string()}).optional(),
  /** The move the feedback suggested, in a few words. Omitted: unknown. */
  suggestion: z.string().trim().min(1).optional(),
  /** Written by an admin, or generated from the feedback. */
  source: SimTeachPuzzleTailoredSourceSchema,
});

/** Where a tailored puzzle came from. */
export type SimTeachPuzzleTailored = z.infer<
  typeof SimTeachPuzzleTailoredSchema
>;

export const SimTeachPuzzleSchema = z
  .object({
    /** Shown as the sidebar heading. */
    title: z.string().trim().min(1, 'Required'),
    /** One sentence on what the puzzle is; fits on the dashboard card. */
    summary: z.string(),
    /**
     * Longer context on the move being practiced, shown in the green room
     * before the session. Omitted: none.
     */
    briefing: z.string().optional(),
    /** Lesson or puzzle. Omitted: `puzzle`. */
    kind: SimTeachPuzzleKindSchema.optional(),
    /** Labels shown as badges; display only. Omitted: none. */
    tags: z.array(z.string().trim().min(1)).optional(),
    /**
     * Whether it's recommended practice: badged and listed first. Omitted:
     * not recommended.
     */
    recommended: z.boolean().optional(),
    /**
     * Its place among the dashboard section's puzzles, lowest first, after
     * `recommended`. Omitted: after the ordered ones, by id.
     */
    order: z.number().int().optional(),
    /**
     * Whether learners see it on the dashboard. Omitted: not published (only
     * admins reach it, from the editor).
     */
    published: z.boolean().optional(),
    /**
     * Set on a puzzle made from one section's feedback: its section leaders
     * see it before it's published. Omitted: an ordinary puzzle.
     */
    tailored: SimTeachPuzzleTailoredSchema.optional(),
    /** Which slides the session shows. Omitted: the course's default deck. */
    slides: SimTeachPuzzleSlidesSchema.optional(),
    /**
     * Whether the session mutes the teacher's mic each time a student starts
     * speaking, so the teacher takes each turn by unmuting.
     */
    autoPause: z.boolean(),
    /** What winning looks like. Required for a puzzle. Omitted: none. */
    goal: SimTeachPuzzleGoalSchema.optional(),
    /** The cards, in display order. A puzzle has none. */
    cards: z.array(SimTeachPuzzleCardSchema),
    /** Seats with their own starting minds. Omitted: none. */
    students: StudentSeedsSchema.optional(),
    /**
     * The scripted turns the session replays on connect, in order. It ends on
     * a student turn or a silence turn. Omitted: the session starts fresh.
     */
    opening: z.array(SimTeachPuzzleOpeningTurnSchema).min(1).optional(),
  })
  .superRefine((puzzle, ctx) => {
    const speakers = (puzzle.opening ?? []).map(turn =>
      turn.speaker.toLowerCase(),
    );
    speakers.slice(0, -1).forEach((speaker, index) => {
      if (speaker === SIMTEACH_PUZZLE_SILENCE_SPEAKER) {
        ctx.addIssue({
          code: 'custom',
          path: ['opening', index, 'speaker'],
          message: 'Silence can only be the last turn',
        });
      }
    });
    if (speakers.at(-1) === SIMTEACH_PUZZLE_TEACHER_SPEAKER) {
      ctx.addIssue({
        code: 'custom',
        path: ['opening', speakers.length - 1, 'speaker'],
        message: 'End on a student turn or silence',
      });
    }
    if ((puzzle.kind ?? 'puzzle') === 'puzzle') {
      if (puzzle.goal === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['goal'],
          message: 'A puzzle needs a goal',
        });
      }
      if (puzzle.cards.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['cards'],
          message: 'Only lessons have cards',
        });
      }
    }
    const seenIds = new Set<string>();
    puzzle.cards.forEach((card, index) => {
      const issue = (field: string, message: string) => {
        ctx.addIssue({code: 'custom', path: ['cards', index, field], message});
      };
      if (seenIds.has(card.id)) issue('id', 'Duplicate card id');
      seenIds.add(card.id);
      if (
        card.cumulative &&
        (card.kind !== 'step' || card.completedBy !== 'teacher')
      ) {
        issue('cumulative', 'Only a teacher step can be cumulative');
      }
      if (card.kind === 'step') {
        if (card.after !== undefined)
          issue('after', 'Only surprises have this');
        if (card.effect !== undefined)
          issue('effect', 'Only surprises have this');
        if (card.timeoutSeconds !== undefined) {
          issue('timeoutSeconds', 'Only surprises have this');
        }
        return;
      }
      const isEarlierStep = puzzle.cards
        .slice(0, index)
        .some(earlier => earlier.kind === 'step' && earlier.id === card.after);
      if (!isEarlierStep) issue('after', 'Pick an earlier step');
    });
  });

/** A SimTeach puzzle document. */
export type SimTeachPuzzle = z.infer<typeof SimTeachPuzzleSchema>;

/**
 * Path of one SimTeach puzzle document.
 *
 * @param args.courseId - The course the puzzle belongs to.
 * @param args.puzzleId - The puzzle's id.
 * @return `course/{courseId}/simTeachPuzzles/{puzzleId}`.
 * @throws Never.
 */
export function simTeachPuzzleDocPath({
  courseId,
  puzzleId,
}: {
  courseId: string;
  puzzleId: string;
}): string {
  return `${simTeachPuzzlesCollectionPath({courseId})}/${puzzleId}`;
}

/**
 * Path of a course's SimTeach puzzle collection.
 *
 * @param args.courseId - The course whose puzzles to address.
 * @return `course/{courseId}/simTeachPuzzles`.
 * @throws Never.
 */
export function simTeachPuzzlesCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `course/${courseId}/${SIMTEACH_PUZZLES_COLLECTION}`;
}
